import { createFileRoute, Link } from '@tanstack/react-router'
import { BookOpen, Music, Brain, Calendar } from 'lucide-react'

export const Route = createFileRoute('/app/onboarding')({
  component: OnboardingPage,
})

function OnboardingPage() {
  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 terminal-border p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [WELCOME TO JINGLEARN]
          </h1>
          <p className="text-trail-tan font-mono text-sm sm:text-base">
            Learn Spanish through bilingual songs and spaced repetition flashcards
          </p>
        </div>

        {/* Route Explanations */}
        <div className="space-y-6">
          {/* Flashcard Decks - START HERE */}
          <Link
            to="/app/decks"
            className="terminal-border p-4 sm:p-6 bg-black hover:border-terminal-green transition-all block cursor-pointer relative"
          >
            <span className="absolute top-2 right-2 text-xs font-mono px-2 py-1 bg-terminal-green text-black border border-terminal-green animate-pulse">
              START HERE
            </span>
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-sky-blue flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-sky-blue font-mono mb-2">
                  [FLASHCARD DECKS]
                </h2>
                <p className="text-trail-tan font-mono text-sm">
                  Manage your learning with flashcard decks. System decks are curated collections you
                  can copy to your personal library. Create custom decks, adjust review frequency, and
                  track your progress. Each deck contains bilingual flashcards with Spanish on the
                  front and English translations on the back.
                </p>
              </div>
            </div>
          </Link>

          {/* Schedule */}
          <Link
            to="/app/schedule"
            className="terminal-border p-4 sm:p-6 bg-black hover:border-terminal-green transition-all block cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <Calendar className="w-8 h-8 text-sky-blue flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-sky-blue font-mono mb-2">
                  [SCHEDULE]
                </h2>
                <p className="text-trail-tan font-mono text-sm">
                  View your learning schedule and upcoming reviews. See which decks are due for practice
                  and plan your study sessions. Track daily streaks and maintain consistent progress.
                </p>
              </div>
            </div>
          </Link>

          {/* Flashcard Review */}
          <Link
            to="/app/flashcards"
            className="terminal-border p-4 sm:p-6 bg-black hover:border-terminal-green transition-all block cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <Brain className="w-8 h-8 text-terminal-green flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-terminal-green font-mono mb-2">
                  [FLASHCARD REVIEW]
                </h2>
                <p className="text-trail-tan font-mono text-sm">
                  Practice flashcards using spaced repetition (Anki algorithm). Cards are scheduled
                  based on how well you know them. Mark cards as easy, good, hard, or again. The
                  system automatically optimizes review intervals to maximize retention and minimize
                  study time.
                </p>
              </div>
            </div>
          </Link>

          {/* Songs */}
          <Link
            to="/app/songs"
            className="terminal-border p-4 sm:p-6 bg-black hover:border-terminal-green transition-all block cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <Music className="w-8 h-8 text-sky-blue flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-sky-blue font-mono mb-2">
                  [SONGS]
                </h2>
                <p className="text-trail-tan font-mono text-sm">
                  Access your personal song collection. View songs you've added and manage your favorites.
                  Create flashcards from song lyrics and practice with bilingual content.
                </p>
              </div>
            </div>
          </Link>

          {/* Song Library */}
          <Link
            to="/app/songs/library"
            className="terminal-border p-4 sm:p-6 bg-black hover:border-terminal-green transition-all block cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <Music className="w-8 h-8 text-terminal-green flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-terminal-green font-mono mb-2">
                  [SONG LIBRARY]
                </h2>
                <p className="text-trail-tan font-mono text-sm">
                  Browse our collection of bilingual Spanish songs. Each song displays Spanish lyrics
                  with English translations side-by-side. Search by lyrics, verb, or title. Listen to
                  audio versions and add songs to your personal collection.
                </p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}
